import React from "react";
import { FieldGroupRow, FieldGroupRowStatus } from "../../../models/intake";
import FormConstants from "./DataEntryFieldsConstants";
import DateField from "../../../common/formFields/DateField";
import { DropdownOption } from "../../../common/formFields/DropdownField";
import { generateUpdateDisclosureInformationObject } from "../../../common/utils/appUtils";
import { DEFAULT_DROPDOWN_VALUE, DEFAULT_MIN_DATE, INVALID_DATE } from "../../../common/constants/ApiConstants";
import { isDateNotSqlOverflow } from "./validations/localValidations";
import "./QuickEntryDisclosureRow.scss";

/**
 * Quick Entry's Disclosure Information capture (Spec #35135) - the Disclosure
 * Accounting (request type 66) parity row.
 *
 * COMPOSITE ROW, not six rows: models/intake.ts's FieldGroupRow carries exactly
 * one `value: string | null`, so a multi-field capture has to be a composite -
 * the same shape PatientInfoRow / RequestorInfoRow already use (a single row
 * with `value: null` and a derived status, whose rich content mounts in
 * DataEntryDetails.renderQuickEntryRowContent).
 *
 * DEFERRED PERSISTENCE (AC4, architectural invariant): this component NEVER
 * performs I/O. Every change is an optimistic LOCAL echo into the caller's
 * draft state; the ONE disclosure write happens at Confirm All, inside
 * persistQuickEntrySelectionsToTransaction's awaited `writes` gate. Quick Entry
 * removed inline persistence deliberately (see persistQuickEntryReleaseInfo's
 * own doc comment - a blur-save reintroduces the "clean slate" leak, where a
 * document replace can't discard the draft and Classic Mode can see in-progress
 * answers before Confirm All ran). Do not add an onBlur save here.
 *
 * HIPAA (.claude/rules/hipaa-compliance.md): Protocol Name, Protocol Number and
 * Special Notes are PHI-bearing free text. They are never logged and never
 * interpolated into a URL by this module.
 */

export const DISCLOSURE_INFO_ROW_ID = "disclosureInfo";

export const DISCLOSURE_INFO_ROW_LABEL = "Disclosure Information";

/**
 * The specialist's in-progress disclosure entry. Local-only until Confirm All -
 * intentionally NOT redux-form state (Classic's disclosure fields live in
 * DATA_ENTRY_FIELDS_FORM; Quick Entry keeps its own draft so a document replace
 * can discard it and Classic Mode never sees an uncommitted answer).
 */
export interface QuickEntryDisclosureDraft {
    /** Required. Disclosure reason id from state.dataEntryState.disclosureReasons. */
    readonly disclosureReason?: number | string;
    /** Required. */
    readonly disclosureDate?: Date | string;
    readonly pagesDisclosed?: string;
    readonly protocolName?: string;
    readonly protocolNumber?: string;
    readonly specialNotes?: string;
    /**
     * REQUIRED (>= 1) for Disclosure Accounting - the `requestedItemID`s of the PHI
     * codes the specialist has selected. Classic enforces the same rule:
     * localValidations.ts's type-66 branch errors with "At least one PHI code must
     * be selected" when this is empty.
     *
     * `undefined` means UNTOUCHED THIS SESSION, which is NOT the same as "none
     * selected". Untouched falls back to the server's own selection (the entries in
     * `selectedPHICodes` whose `isRetrieved` is true) for display, and contributes
     * NO write at Confirm All - the same "null = untouched, skip persist" semantics
     * `quickEntryReleaseInfo` uses in the gate. Without that distinction, reopening
     * a transaction that already had codes selected and committing without touching
     * them would diff to "delete everything".
     */
    readonly selectedPhiCodeIds?: readonly number[];
}

export const EMPTY_DISCLOSURE_DRAFT: QuickEntryDisclosureDraft = {};

/** One row of state.dataEntryState.selectedPHICodes. `isRetrieved` is the selected flag. */
export interface PhiCodeOption {
    readonly requestedItemID: number;
    readonly code: string;
    readonly description: string;
    readonly isRetrieved?: boolean;
}

/** The server's current selection - the ids whose `isRetrieved` is true. */
export function serverSelectedPhiCodeIds(options: readonly PhiCodeOption[] | undefined): readonly number[] {
    return (options ?? []).filter(o => o.isRetrieved).map(o => o.requestedItemID);
}

/**
 * What the row should DISPLAY as selected: the draft when the specialist has
 * touched it, otherwise the server's own selection.
 */
export function effectivePhiCodeIds(
    draft: QuickEntryDisclosureDraft | null | undefined,
    options: readonly PhiCodeOption[] | undefined,
): readonly number[] {
    return draft?.selectedPhiCodeIds ?? serverSelectedPhiCodeIds(options);
}

/**
 * The per-code writes Confirm All must emit (AC: diff, don't re-send everything).
 * `updatePHIDisclosed` is per-code with a delete flag rather than a set-replace, so
 * the gate compares the draft against the server's `isRetrieved` state and emits one
 * call per CHANGED code only - no redundant churn on the PHIDisclosed audit table.
 *
 * Returns [] when the draft is untouched, which is what makes an unmodified reopen
 * contribute no writes.
 */
export function diffPhiCodeSelections(
    draft: QuickEntryDisclosureDraft | null | undefined,
    options: readonly PhiCodeOption[] | undefined,
): readonly { readonly requestedItemID: number; readonly isDelete: boolean }[] {
    const drafted = draft?.selectedPhiCodeIds;
    if (drafted == null) {
        return [];
    }
    const before = new Set(serverSelectedPhiCodeIds(options));
    const after = new Set(drafted);
    const changes: { requestedItemID: number; isDelete: boolean }[] = [];
    (options ?? []).forEach(o => {
        const wasSelected = before.has(o.requestedItemID);
        const isSelected = after.has(o.requestedItemID);
        if (wasSelected !== isSelected) {
            changes.push({ requestedItemID: o.requestedItemID, isDelete: !isSelected });
        }
    });
    return changes;
}

/**
 * The date half of the Confirm-All validity gate. Mirrors Classic's inline
 * onBlurDisclosureInformationFields check (DataEntryDetailsRightPane) exactly -
 * present, not the INVALID_DATE sentinel, parseable, and inside SQL's
 * datetime range - just evaluated once at the gate instead of on every blur.
 */
export function isValidDisclosureDate(value?: Date | string): boolean {
    if (!value) {
        return false;
    }
    if (value === INVALID_DATE) {
        return false;
    }
    const parsed = value instanceof Date ? value : new Date(String(value));
    if (isNaN(parsed.getTime())) {
        return false;
    }
    return isDateNotSqlOverflow(parsed);
}

/**
 * AC5: Reason + a valid Date + at least one PHI code are required; Pages, Protocol
 * name/number and Special notes are optional.
 *
 * The PHI requirement mirrors Classic, which blocks the same condition for request
 * type 66 - localValidations.ts's type-66 branch: "At least one PHI code must be
 * selected". PHI codes are the disclosure-accounting audit record of what was
 * actually disclosed, which is the point of the request type; they are NOT the
 * "Sensitive Information" checklist (that one IS excluded for type 66, via
 * DataEntryDetailsRightPane's isVisibleSensitiveInfoToRelease).
 *
 * `phiOptions` is needed because an untouched draft inherits the server's selection
 * (see QuickEntryDisclosureDraft.selectedPhiCodeIds) - a reopened transaction that
 * already has codes must count as complete without the specialist re-ticking them.
 */
export function isDisclosureDraftComplete(
    draft: QuickEntryDisclosureDraft | null | undefined,
    phiOptions?: readonly PhiCodeOption[],
): boolean {
    if (!draft) {
        return false;
    }
    const reason = draft.disclosureReason;
    const hasReason = reason !== undefined && reason !== null && reason !== "" && String(reason) !== DEFAULT_DROPDOWN_VALUE;
    const hasPhi = effectivePhiCodeIds(draft, phiOptions).length > 0;
    return hasReason && isValidDisclosureDate(draft.disclosureDate) && hasPhi;
}

/**
 * Maps the disclosure draft into the generic Quick Entry row-list contract,
 * mirroring mapPatientInfoToFieldGroupRow / mapRequestorInfoToFieldGroupRow.
 *
 * The status derivation IS the Confirm-All gate (AC8): NeedsEntry makes
 * isFieldGroupRowOutstanding true, which makes getQuickEntryOutstandingCount()
 * >= 1, which makes the EXISTING, unmodified isQuickEntryConfirmAllEnabled()
 * return false and the footer read "Next Action Required (N)". No
 * disclosure-specific gate code is written anywhere - it falls out of the row
 * contract.
 */
export function mapDisclosureInfoToFieldGroupRow(
    draft: QuickEntryDisclosureDraft | null | undefined,
    badgeNumber: number,
    phiOptions?: readonly PhiCodeOption[],
): FieldGroupRow {
    return {
        id: DISCLOSURE_INFO_ROW_ID,
        badgeNumber,
        status: isDisclosureDraftComplete(draft, phiOptions) ? FieldGroupRowStatus.Confirmed : FieldGroupRowStatus.NeedsEntry,
        label: DISCLOSURE_INFO_ROW_LABEL,
        // Composite row - the six fields render in renderQuickEntryRowContent.
        value: null,
        // AC12: no AI pre-fill of disclosure fields in v1 - manual entry only.
        isAiPreSelected: false,
    };
}

/**
 * Builds the EXACT Classic payload (AC4a): generateUpdateDisclosureInformationObject
 * keyed by the same form constants Classic's own values object uses, wrapped in
 * the { transactionID, data } envelope updateDisclosureInformationActionCreator
 * expects. No new endpoint, no reshaping.
 */
export function buildDisclosureUpdatePayload(draft: QuickEntryDisclosureDraft, transactionID: string) {
    return {
        transactionID,
        data: generateUpdateDisclosureInformationObject({
            [FormConstants.DISCLOSURE_DATE]: draft.disclosureDate,
            [FormConstants.DISCLOSURE_REASON]: draft.disclosureReason,
            [FormConstants.PROTOCOL_NUMBER]: draft.protocolNumber,
            [FormConstants.SPECIAL_NOTES]: draft.specialNotes,
            [FormConstants.PAGES_DISCLOSED]: draft.pagesDisclosed,
            [FormConstants.PROTOCOL_NAME]: draft.protocolName,
        }),
    };
}

/**
 * Seeds the draft from the transaction's already-saved disclosure record so a
 * reopened Disclosure Accounting transaction shows what was persisted instead of
 * a blank capture. Same "local override ?? server value" shape
 * getQuickEntryReleaseInfo / getQuickEntrySensitiveInfo already use. Key names
 * mirror Classic's own initialValues mapping (DataEntryDetailsRightPane).
 */
export function disclosureDraftFromTransaction(disclosure: any): QuickEntryDisclosureDraft {
    if (!disclosure) {
        return EMPTY_DISCLOSURE_DRAFT;
    }
    return {
        disclosureReason: disclosure.disclosureReasonID ?? undefined,
        disclosureDate: disclosure.disclosureDate ?? undefined,
        pagesDisclosed: disclosure.numberOfPage ?? undefined,
        protocolName: disclosure.protocolName ?? undefined,
        protocolNumber: disclosure.protocolnumber ?? undefined,
        specialNotes: disclosure.specialNote ?? undefined,
    };
}

export interface QuickEntryDisclosureRowProps {
    readonly draft: QuickEntryDisclosureDraft;
    /** state.dataEntryState.disclosureReasons - already normalized by the reducer's generatePickerOptions. */
    readonly disclosureReasons: readonly DropdownOption[];
    /**
     * state.dataEntryState.selectedPHICodes - the PHI codes available for this
     * transaction, each carrying the server's own `isRetrieved` selection flag.
     * Already fetched on mount by DataEntryDetails.initialAPICalls
     * (getSelectedPHICodes); this row adds no new fetch.
     */
    readonly phiCodes?: readonly PhiCodeOption[];
    /** Local-state echo only. The caller must NOT persist from here (AC4). */
    readonly onChange: (patch: Partial<QuickEntryDisclosureDraft>) => void;
}

/**
 * Field max lengths mirror Classic's own Accordion.tsx disclosure section so the
 * two modes can't accept values the other rejects.
 */
const MAX_LENGTHS = {
    specialNotes: 256,
    pagesDisclosed: 5,
    protocolName: 70,
    protocolNumber: 20,
};

const QuickEntryDisclosureRow: React.FC<QuickEntryDisclosureRowProps> = (props) => {
    const { draft, onChange } = props;
    const phiCodes = props.phiCodes ?? [];
    // Untouched draft shows the server's own selection - see selectedPhiCodeIds.
    const selectedPhiIds = effectivePhiCodeIds(draft, phiCodes);

    // Toggling writes the FULL resulting id list into the draft (not a delta), which
    // is what converts an untouched draft into an explicit one and makes the
    // Confirm-All diff meaningful. Local state only - no I/O here.
    const onTogglePhiCode = (requestedItemID: number) => {
        const next = selectedPhiIds.includes(requestedItemID)
            ? selectedPhiIds.filter(id => id !== requestedItemID)
            : [...selectedPhiIds, requestedItemID];
        onChange({ selectedPhiCodeIds: next });
    };

    return (
        <div className="qe-row__body qe-disclosure">
            <div className="qe-row__label">{DISCLOSURE_INFO_ROW_LABEL}</div>
            <div className="qe-row__value qe-disclosure__fields">
                <div className="qe-disclosure__field" data-field={FormConstants.DISCLOSURE_REASON}>
                    <label htmlFor="qe-disclosure-reason">
                        Reason for disclosure <span className="qe-disclosure__req" aria-hidden="true">*</span>
                    </label>
                    <select
                        id="qe-disclosure-reason"
                        name="disclosureReason"
                        required
                        value={draft.disclosureReason ?? ""}
                        onChange={(e) => onChange({ disclosureReason: e.target.value ? Number(e.target.value) : undefined })}
                    >
                        <option value="">Select a reason</option>
                        {props.disclosureReasons.map((reason, index) => (
                            <option key={`${String(reason.value ?? "")}-${index}`} value={String(reason.value ?? "")}>{reason.label}</option>
                        ))}
                    </select>
                </div>
                <div className="qe-disclosure__field" data-field={FormConstants.DISCLOSURE_DATE}>
                    <DateField
                        label="Disclosure date"
                        placeholder="mm/dd/yyyy"
                        required
                        errMsg=""
                        minDate={DEFAULT_MIN_DATE}
                        maxDate={new Date()}
                        value={draft.disclosureDate}
                        onChange={(value) => onChange({ disclosureDate: value })}
                    />
                </div>
                {/* Protocol name/number are a pair and sit on the SAME grid row. Five
                    half-width fields into a 2-column grid always leaves one cell empty,
                    so the order here decides which field gets orphaned: keeping the two
                    Protocol inputs adjacent puts the gap after "Number of pages
                    disclosed", which reads as a standalone value, instead of splitting
                    the pair across rows with a hole beside Protocol number. */}
                <div className="qe-disclosure__field" data-field={FormConstants.PROTOCOL_NAME}>
                    <label htmlFor="qe-disclosure-protocol-name">Protocol name</label>
                    <input
                        id="qe-disclosure-protocol-name"
                        name="protocolName"
                        type="text"
                        maxLength={MAX_LENGTHS.protocolName}
                        placeholder="add name"
                        value={draft.protocolName ?? ""}
                        onChange={(e) => onChange({ protocolName: e.target.value })}
                    />
                </div>
                <div className="qe-disclosure__field" data-field={FormConstants.PROTOCOL_NUMBER}>
                    <label htmlFor="qe-disclosure-protocol-number">Protocol number</label>
                    <input
                        id="qe-disclosure-protocol-number"
                        name="protocolNumber"
                        type="text"
                        maxLength={MAX_LENGTHS.protocolNumber}
                        placeholder="123456789"
                        value={draft.protocolNumber ?? ""}
                        onChange={(e) => onChange({ protocolNumber: e.target.value })}
                    />
                </div>
                <div className="qe-disclosure__field" data-field={FormConstants.PAGES_DISCLOSED}>
                    <label htmlFor="qe-disclosure-pages">Number of pages disclosed</label>
                    <input
                        id="qe-disclosure-pages"
                        name="pagesDisclosed"
                        type="text"
                        maxLength={MAX_LENGTHS.pagesDisclosed}
                        placeholder="12345"
                        value={draft.pagesDisclosed ?? ""}
                        onChange={(e) => onChange({ pagesDisclosed: e.target.value })}
                    />
                </div>
                <div className="qe-disclosure__field qe-disclosure__field--wide" data-field={FormConstants.SPECIAL_NOTES}>
                    <label htmlFor="qe-disclosure-special-notes">Special notes</label>
                    <input
                        id="qe-disclosure-special-notes"
                        name="specialNotes"
                        type="text"
                        maxLength={MAX_LENGTHS.specialNotes}
                        placeholder="add notes"
                        value={draft.specialNotes ?? ""}
                        onChange={(e) => onChange({ specialNotes: e.target.value })}
                    />
                </div>
                {/* PHI codes - REQUIRED (>= 1) for Disclosure Accounting, the same rule
                    Classic enforces via localValidations' type-66 branch ("At least one
                    PHI code must be selected"). Classic renders this list inside its own
                    Disclosure Information accordion (Accordion.tsx, the FormConstants.PHI
                    Field), so it belongs in this composite row rather than a separate one.

                    Distinct from "Sensitive Information" despite both reading as
                    sensitive-data sections: that checklist IS excluded for type 66
                    (isVisibleSensitiveInfoToRelease), while PHI codes are the disclosure
                    audit record of what was actually disclosed - the point of the type.

                    Local echo only, like every other control here: toggling emits no I/O.
                    The per-code writes are diffed and emitted at Confirm All
                    (diffPhiCodeSelections). */}
                <div className="qe-disclosure__field qe-disclosure__field--wide qe-disclosure__phi" data-field={FormConstants.PHI}>
                    <span className="qe-disclosure__phi-label">
                        PHI disclosed <span className="qe-disclosure__req">*</span>
                    </span>
                    {phiCodes.length === 0 ? (
                        <span className="qe-disclosure__phi-empty">No PHI codes configured for this facility.</span>
                    ) : (
                        <ul className="qe-disclosure__phi-list">
                            {phiCodes.map(option => {
                                const inputId = `qe-disclosure-phi-${option.requestedItemID}`;
                                const checked = selectedPhiIds.includes(option.requestedItemID);
                                return (
                                    <li key={option.requestedItemID} className="qe-disclosure__phi-item">
                                        <input
                                            id={inputId}
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => onTogglePhiCode(option.requestedItemID)}
                                        />
                                        <label htmlFor={inputId}>
                                            <span className="qe-disclosure__phi-code">{option.code}</span>
                                            {option.description ? (
                                                <span className="qe-disclosure__phi-desc">{option.description}</span>
                                            ) : null}
                                        </label>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
};

export default QuickEntryDisclosureRow;
